/**
 * Flight Status Mapper Utility
 * 
 * Maps raw flight hop status messages from the backend to user-friendly labels for display.
 */

export interface StatusMapping {
  label: string;
  time?: string; // Optional time information extracted from status message
}

export class FlightStatusMapper {
  private static readonly STATUS_MAPPINGS: Record<string, StatusMapping> = {
    // Basic Status Messages
    'Booked': { label: 'SCHEDULED' },
    'Unknown': { label: 'NA' },
    'Cancelled': { label: 'CANCELLED' },
    'Diverted': { label: 'DIVERTED' },
    'Redirected': { label: 'REDIRECTED' },
    
    // Scheduled Status
    'Scheduled On-time': { label: 'SCHEDULED' },
    
    // Active/En Route Status
    'En Route, Departed On-time': { label: 'DEPARTED ON-TIME' },
    'En Route, Arriving On-time': { label: 'ARRIVING ON-TIME' },
    'Inconsistent Information': { label: 'NA' },
    
    // Landed Status
    'Arrived On-time': { label: 'ARRIVED ON-TIME' },
    
    // Issue Types
    'Cancellation': { label: 'CANCELLATION' },
    'Late Arrival': { label: 'ARRIVED DELAYED' },
    'Late Departure': { label: 'DEPARTED DELAYED' },
    'Connection Issue': { label: 'CONNECTION-ISSUE' },
  };

  private static readonly STATUS_PATTERNS: Array<{ pattern: RegExp; label: string; extractTime?: boolean }> = [
    // Diverted patterns
    { pattern: /^Diverted(\s+to\s+[A-Z]{3})?$/i, label: 'DIVERTED' },
    
    // Delay patterns
    { pattern: /^Departure\s+Delayed\s+(.+)$/i, label: 'DEPARTURE DELAYED', extractTime: true },
    { pattern: /^En\s+Route,\s+Departure\s+Delayed\s+(.+)$/i, label: 'DEPARTURE DELAYED', extractTime: true },
    { pattern: /^En\s+Route,\s+Arrival\s+Delayed\s+(.+)$/i, label: 'ARRIVAL DELAYED', extractTime: true },
    { pattern: /^Arrived\s+Delayed\s+(.+)$/i, label: 'ARRIVED DELAYED', extractTime: true },
  ];

  /**
   * Maps a raw flight status message to a display label
   */
  public static mapStatus(statusMessage: string, statusIndicator?: string): StatusMapping {
    if (!statusMessage) {
      return { label: 'UNKNOWN' };
    }

    // Check direct mappings first
    if (this.STATUS_MAPPINGS[statusMessage]) {
      return this.STATUS_MAPPINGS[statusMessage];
    }

    // Check pattern-based mappings
    for (const pattern of this.STATUS_PATTERNS) {
      const match = statusMessage.match(pattern.pattern);
      if (match) {
        const result: StatusMapping = {
          label: pattern.label
        };
        
        // Extract time information if pattern supports it
        if (pattern.extractTime && match[1]) {
          result.time = this.formatTimeString(match[1].trim());
        }
        
        return result;
      }
    }

    // Fallback to unknown
    return {
      label: 'UNKNOWN'
    };
  }

  /**
   * Formats time strings from status messages to be more concise
   */
  private static formatTimeString(timeStr: string): string {
    // Convert common time formats to shorter versions
    return timeStr
      .replace(/(\d+)H\s*(\d+)MINS?/i, '$1H$2M')  // "1H 15MINS" -> "1H15M"
      .replace(/(\d+)\s*MINS?/i, '$1M')           // "30MINS" or "30mins" -> "30M"
      .replace(/(\d+)\s*HOURS?/i, '$1H')          // "2HOURS" -> "2H"
      .toUpperCase();
  }

  /**
   * Gets all possible status labels for reference
   */
  public static getAllPossibleLabels(): string[] {
    return [
      'SCHEDULED', 'UNKNOWN', 'CANCELLED', 'DIVERTED', 'REDIRECTED',
      'DEPARTURE DELAYED', 'DEPARTED ON-TIME', 'ARRIVAL DELAYED', 
      'ARRIVING ON-TIME', 'ARRIVED ON-TIME', 'ARRIVED DELAYED',
      'CANCELLATION', 'CONNECTION-ISSUE'
    ];
  }

  /**
   * Example usage and testing method
   * This demonstrates how various backend status messages get mapped to labels
   */
  public static getExampleMappings(): Array<{ input: string; output: string }> {
    const examples = [
      'Booked',
      'Scheduled On-time',
      'Departure Delayed 30MINS',
      'Departure Delayed 1H 15MINS',
      'En Route, Departed On-time',
      'En Route, Departure Delayed 20MINS',
      'En Route, Arrival Delayed 50MINS',
      'Arrived On-time',
      'Arrived Delayed 25MINS',
      'Cancelled',
      'Diverted',
      'Diverted to JFK',
      'Redirected',
      'Inconsistent Information',
      'Unknown',
      // Issue Types
      'Cancellation',
      'Late Arrival',
      'Late Departure',
      'Connection Issue'
    ];

    return examples.map(status => {
      const mapped = this.mapStatus(status);
      const output = mapped.time ? `${mapped.label} ${mapped.time}` : mapped.label;
      return {
        input: status,
        output: output
      };
    });
  }
}